import {
  Box,
  lighten,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "common/components";
import dayjs, { Dayjs } from "dayjs";
import { ReactElement } from "react";

/**
 * A Period is rendered as a colored rectangle, with rounded edges,
 * above the timeline.
 * A Period has a description which is displayed in the center
 * of the rectangle by default, and has an optional *fromLabel* and *toLabel*
 * which are displayed as permanent tooltips above the left or right border
 * of the rectangle.
 */
export interface Period {
  /** Text that is displayed inside the rectangle that represents the period */
  label: string;

  /** Text that is displayed inside the rectangle that represents the period,
   * under the label */
  subLabel?: string;

  /** The prefix used to generate a data-cy for the elements representing this period.
   * The actual data-cy of the period box will be `${dataCy}Period`.
   * The data-cy of the 'fromLabel' will be `${dataCy}PeriodLabelFrom`.
   * The data-cy of the 'toLabel' will be `${dataCy}PeriodLabelTo`. */
  dataCy: string;

  /** (optional) label and subLabel alignment. Defaults to "center" */
  labelAlignment?:
    | "start"
    | "end"
    | "left"
    | "right"
    | "center"
    | "justify"
    | "match-parent";

  /** (optional) The description is displayed as a Tooltip that pops up when the mouse hovers
   * over the rectangle that represents the period */
  description?: string;

  /** (optional) Placement for the tooltip that displays the description. Defaults to "bottom" */
  descriptionPlacement?:
    | "left"
    | "right"
    | "bottom"
    | "top"
    | "bottom-end"
    | "bottom-start"
    | "left-end"
    | "left-start"
    | "right-end"
    | "right-start"
    | "top-end"
    | "top-start"
    | undefined;

  /** The horizontal position on the timeline at which the period starts.
   * Must be between positionMin and positionMax. */
  positionFrom: number;

  /** The horizontal position on the timeline at which the period ends.
   * Must be between positionMin and positionMax. */
  positionTo: number;

  /** (optional) A text that is displayed as a tooltip above the left side of the rectangle
   * that represents the period. It is meant to describe the beginning of the period. */
  labelFrom?: string;

  /** (optional) Width of the tooltip that displays the *labelFrom*, in pixels.
   * Defaults to 60px. */
  labelFromWidth?: number;

  /** (optional) A text that is displayed as a tooltip above the right side of the rectangle
   * that represents the period. It is meant to describe the end of the period.
   * The tooltip is displayed as white text inside a dark grey bubble. */
  labelTo?: string;

  /** (optional) Width of the tooltip that displays the *labelTo*, in pixels.
   * Defaults to 60px. */
  labelToWidth?: number;

  /** (optional) If set to *true*, no colored rectangle is displayed. But you can still
   * use labelFrom and labelTo to display permanent tooltips. Default to false. */
  isInvisible?: boolean;

  /** (optional) Opacity of the colored rectangle that represents the period.
   * Set to a number between 0 (completely transparent) and 1 (completely opaque).
   * Defaults to 1. */
  opacity?: number;

  /** (optional) Text color used for the Period's label and sub-label.
   * Defaults to theme.palette.primary.dark */
  textColor?: string;

  /** (optional) Base fill color for the rounded rectangle that represents the
   * period. Defaults to theme.palette.primary.main */
  fillColor?: string;
}

/**
 * A PeriodLevel is a set of Periods that share the same height.
 */
export interface PeriodLevel {
  /** Periods that are on that level */
  periods: (Period | undefined)[] | undefined;

  /** (optional) Height of the level, in Pixels. Defaults to
   * the value of FlexTimelineProps.defaultPeriodLevelHeight
   */
  height?: number;
}

/**
 * Helper function that creates an Array of PeriodLevels based
 * on the provided arrays of Period objects
 * @param periodsForLevels One or more arrays containing Period objects
 */
export function makePeriodLevels(
  ...periodsForLevels: ((Period | undefined)[] | undefined)[]
): PeriodLevel[] {
  const newArray = [];
  for (const periodForLevel of periodsForLevels) {
    const newPeriodLevel: PeriodLevel = {
      periods: periodForLevel,
    };
    newArray.push(newPeriodLevel);
  }
  return newArray;
}

/**
 * A Mark is rendered as a small arrow and a label under the timeline.
 * A Mark is intended to be used to display dates below the timeline.
 */
export interface Mark {
  /** The horizontal position of the mark on the timeline.
   * Must be between positionMin and positionMax. */
  position: number;

  /** The label that is displayed using a permanent tooltip under the timeline */
  label: string;

  /* Prefix used to generate the data-cy.
   * The actual data-cy will be `${dataCy}Mark`
   * Should be set to something simple like 'bindStart', 'regretEnd', etc. */
  dataCy: string;

  /** The date that this mark represents */
  date: Dayjs;

  /** (optional) Set to *true* if you do not want this mark to be displayed
   * under the timeline. Defaults to false. */
  isInvisible?: boolean;

  /** (optional) Vertical position, as a percentage of the period height.
   * Should be a value between 0 and 100.
   * Defaults to 100%, which will result in displaying the mark under the
   * periods. Set to 20% will display the mark just under the timeline. */
  relativeVerticalPositionLabel?: number;
}

/**
 * Helper function to create a Mark from a string representing a date
 *
 * @param position Position of the Mark on the timeline
 * @param date A Dayjs representing a date.
 *   - Mark.label will be set to this date in localized format.
 *   - Mark.date is set to this date.
 *   If date is undefined, the current date will be used.
 * @param dataCy prefix used to generate the data-cy.
 * @param isInvisible (optional) Set to true if this mark should not be displayed
 *   on the FlexTimeline
 * @param relativeVerticalPositionLabel (optional) Set to a number between 0 and 100 to position
 *   the mark label relative to the timeline.
 */
export function newMark(
  position: number,
  date: Dayjs | undefined,
  dataCy: string,
  isInvisible = false,
  relativeVerticalPositionLabel?: number
): Mark {
  const _date = date ?? dayjs();
  return {
    position,
    label: _date.format("ll"),
    dataCy,
    date: _date,
    isInvisible,
    relativeVerticalPositionLabel,
  };
}

/**
 * A Cursor is rendered as a vertical hairline behind the timeline.
 * It is intended to be used to represent the current date on the timeline
 */
export interface Cursor extends Mark {
  /** (optional) A text that describes this mark. Defaults to undefined. */
  description?: string;

  /** (optional) Width of the vertical line representing the cursor, in pixels.
   * Defaults to 2. */
  cursorWidth?: number;

  /** (optional) Vertical position, as a percentage of the period height.
   * Should be a value between 0 and 100.
   * Defaults to 100%, which will result in displaying the mark under the
   * periods. Set to 20% will display the mark just under the timeline. */
  relativeVerticalPositionDescription?: number;

  /** (optional) Width of the tooltip displaying the description, in pixels.
   * Defaults to 300. */
  tooltipWidth?: number;

  /** Text color for Label and description. */
  textColor: string;

  /** Color that is used as color for the vertical hairline,
   * and as background color for the tooltips used to render the description
   * and the date. */
  fillColor: string;
}

/**
 * Helper function to create an Mark from a string representing a date.
 *
 * @param position Position of the cursor on the timeline
 * @param date A string or Dayjs representing a date.
 *   - Mark.label will be set to this date in localized format.
 *   - Mark.date is set to the this date.
 *   If date is undefined, the current date will be used.
 * @param dataCy String used as prefix for the data-cys of the elements that
 *   represent the cursor. The data-cy for the description will be
 *   `${dataCy}CursorDescription`. The data-cy for the label will be
 *   `${dataCy}CursorLabel`
 * @param description A string that is stored in Mark.description
 * @param theme The application's current theme (used to set the colors for
 *   text and background of the shapes)
 * @param relativeVerticalPositionLabel (optional) Set to a number between 0 and 500
 *   to position the mark label relative to the timeline.
 * @param relativeVerticalPositionDescription (optional) Set to a number between 0 and 500
 *   to position the mark's description relative to the timeline.
 * @param tooltipWidth (optional) The width of the tooltip that displays the
 *   description. Defaults to 300px.
 * @param textColor: (optional) Color that is used to render the description
 *   and the date. Defaults to theme.palette.grey[600].
 * @param fillColor: (optional) Color that is used as color for the vertical hairline,
 *   and as background color for the tooltips used to render the description
 *   and the date. Defaults to theme.palette.grey[300].
 */
export function newCursor(
  position: number,
  date: Dayjs | undefined,
  dataCy: string,
  description: string,
  theme: Theme,
  relativeVerticalPositionLabel?: number,
  relativeVerticalPositionDescription?: number,
  tooltipWidth?: number,
  textColor?: string,
  fillColor?: string
): Cursor {
  const _date = date ?? dayjs();
  return {
    dataCy,
    position,
    label: _date.format("ll"),
    date: _date,
    description,
    isInvisible: false,
    relativeVerticalPositionLabel,
    relativeVerticalPositionDescription,
    tooltipWidth,
    textColor: textColor ?? theme.palette.grey[600],
    fillColor: fillColor ?? theme.palette.grey[300],
  };
}

/* Helper function that calculates a position that is proportional
 * to the position of currentDate within the dates of the 2 marks
 */
export function calculateRelativePosition(
  currentDate?: Dayjs,
  firstMark?: Mark,
  secondMark?: Mark
): number {
  if (
    currentDate === undefined ||
    firstMark === undefined ||
    secondMark === undefined
  ) {
    return 0;
  }
  const t1 = currentDate.diff(firstMark.date, "day");
  const t = secondMark.date.diff(firstMark.date, "day");
  const v1 = ((secondMark.position - firstMark.position) * t1) / t;
  return firstMark.position + v1;
}

export interface FlexTimelineProps {
  /** The position that corresponds to the start of the timeline */
  positionMin: number;

  /** The position that corresponds to the end of the timeline */
  positionMax: number;

  /** The periods that will be displayed above the timeline. Each period is
   * represented by a colored box, unless it's *isInvisible* property is *true*.
   * Periods with a higher *level* are displayed on top of (overlapping)
   * periods with a lower *level*.
   * The horizontal position of the period on the timeline is defined by
   * the *positionFrom* and *positionTo* properties.
   */
  periodLevels: PeriodLevel[] | undefined;

  /** (optional) Default height of the period levels, in Pixels. Defaults to 50. */
  defaultPeriodLevelHeight?: number;

  /** The marks that will be displayed under the timeline. Each mark is
   * represented by a small arrow and a label.
   * The horizontal position of the mark on the timeline is defined by the
   * *position* property.
   */
  marks: Mark[] | undefined;

  /** (optional) The cursors are displayed as a vertical line, perpendicular to the timeline
   * that is horizontal. A permanent tooltip above each cursor line displays the
   * *description". A permanent tooltip below the cursor line displays the
   * *label", just like the Marks. A cursor is usually used to represent the
   * current date, or a specific date like the Deactivation Date.
   * No cursors are displayed if this parameter is not provided. */
  cursors?: Cursor[];

  /** (optional) color for the timeline. Defaults to the theme's palette.grey[600] */
  timelineColor?: string;

  /** (optional) width for the timeline, in pixels.
   * Defaults to 10. */
  timelineHeight?: number;

  /** (optional) zIndex value used for the Mark and Period Tooltips. Defaults to 10 */
  tooltipsZIndex?: number;
}

/** This component displays Marks, Periods and a Cursor on a horizontal timeline.
 * - Marks are displayed as small arrows under the timeline,
 *   with a label under the arrow.
 * - The Periods are displayed as colored rounded-rectangles above the timeline.
 *   The Periods can be organized into multiple PeriodLevels.
 *   The Periods in PeriodLevel N+1 are narrower than the Periods on PeriodLevel N,
 *   and Periods on level N+1 are displayed 'on top' of Periods on level N.
 * - The Cursor is displayed as a vertical hairline across the timeline, with
 *   a label below the timeline and a description above the timeline.
 * @see Mark
 * @see Period
 * @see PeriodLevel
 * @see Cursor
 */
export function FlexTimeline({
  positionMin,
  positionMax,
  periodLevels,
  defaultPeriodLevelHeight,
  marks,
  cursors,
  timelineColor,
  timelineHeight = 10,
  tooltipsZIndex = 10,
}: FlexTimelineProps): ReactElement {
  const theme = useTheme();
  const _timelineColor = timelineColor ?? theme.palette.grey[600];

  const totalNumberOfLevels = periodLevels?.length ?? 0;

  /** Margin used on the lect and right side of the timeline (in pixels) */
  const timelineMargin = 10;

  // Default height for the periods, in pixels
  const defaultPeriodHeight = defaultPeriodLevelHeight ?? 50;

  // Default background color for mark tooltips
  const defaultMarkFillColor = theme.palette.background.paper;

  const minToMax = positionMax - positionMin;

  // The height for the Stack that includes all the marks is calculated
  // based on the height of the period level at index 0 (or the default period)
  const marksStackHeight =
    (periodLevels?.at(0)?.height ?? defaultPeriodHeight) / 2.0;

  // Absolute vertical position of the timeline (in pixels)
  // based on the height of the period level at index 0 (or the default period).
  // The value 1.4 was adjusted by trial and error.
  const topBase =
    100 + 1.4 * (periodLevels?.at(0)?.height ?? defaultPeriodHeight);

  /** Helper function that calculates the absolute width of a Box
   * as a fraction of the timeline width) */
  function getBoxWidth(positionFrom: number, positionTo: number): number {
    return (positionTo - positionFrom) / minToMax;
  }

  /** Helper function that calculates the horizontal position of a Box,
   * as a percentage of the timeline width */
  function getBoxLeft(positionFrom: number): string {
    return `${(100 * (positionFrom - positionMin)) / minToMax}%`;
  }

  return (
    <Box
      data-cy="flexTimeline"
      sx={{
        minHeight: "320px",
        minWidth: "640px",
        overflow: "auto",
        position: "relative",
      }}
    >
      <Box
        data-cy="timeline"
        sx={{
          height: `${timelineHeight}px`,
          display: "block",
          position: "absolute",
          left: timelineMargin,
          right: timelineMargin,
          top: `${topBase - timelineHeight / 2.0}px`,
          borderRadius: theme.shape.borderRadius,
          backgroundColor: _timelineColor,
          opacity: "0.33",
        }}
      ></Box>
      {periodLevels?.map((periodLevel, level) => {
        // Height of the period level
        const periodLevelHeight = periodLevel.height ?? defaultPeriodHeight;
        // Vertical position of Stack (in pixels)
        const stackTop = topBase - periodLevelHeight / 2.0;
        return (
          <Stack
            data-cy={`periodLevel${level}`}
            direction="row"
            key={`stack_${level}`}
            sx={{
              display: "block",
              height: `${periodLevelHeight}px`,
              position: "absolute",
              top: `${stackTop}px`,
              left: `${timelineMargin}px`,
              right: `${timelineMargin}px`,
            }}
          >
            {periodLevel.periods?.map((period, index) => {
              // Absolute vertical position of a period
              const periodTop =
                ((1 -
                  calculatePeriodRelativeHeight(totalNumberOfLevels, level) /
                    100) *
                  periodLevelHeight) /
                2.0;
              return (
                <PeriodBox
                  period={period}
                  level={level}
                  totalNumberOfLevels={totalNumberOfLevels}
                  getPeriodBoxWidth={getBoxWidth}
                  getPeriodBoxLeft={getBoxLeft}
                  periodBoxTop={periodTop}
                  zIndex={tooltipsZIndex + 1 + level}
                  key={`periodBox_${level}_${index}`}
                />
              );
            })}
          </Stack>
        );
      })}
      <Stack
        direction="row"
        sx={{
          display: "block",
          height: `${marksStackHeight}px`,
          position: "absolute",
          top: `${topBase}px`,
          left: `${timelineMargin}px`,
          right: `${timelineMargin}px`,
        }}
      >
        {marks?.map((mark, index) => {
          return (
            <MarkBox
              mark={mark}
              getMarkBoxLeft={getBoxLeft}
              zIndex={tooltipsZIndex}
              key={`mark_${index}`}
              labelFillColor={defaultMarkFillColor}
              labelTextColor={_timelineColor}
              labelArrowColor={_timelineColor}
            />
          );
        })}
      </Stack>
      {cursors && (
        <Stack
          direction="row"
          sx={{
            display: "block",
            height: `${marksStackHeight}px`,
            position: "absolute",
            top: `${topBase}px`,
            left: `${timelineMargin}px`,
            right: `${timelineMargin}px`,
          }}
        >
          {cursors?.map((cursor, index) => {
            return (
              <CursorBox
                cursor={cursor}
                getCursorBoxLeft={getBoxLeft}
                zIndex={tooltipsZIndex}
                key={`cursor_${index}`}
                heightAbove={marksStackHeight}
                heightBelow={marksStackHeight}
                fillColor={cursor.fillColor}
                textColor={cursor.textColor}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

interface PeriodBoxProps {
  /** The Period which the PeriodBox is rendering */
  period: Period | undefined;
  /** Level of the PeriodLevel which contains this Period */
  level: number;
  /** The total number of PeriodLevels */
  totalNumberOfLevels: number;
  /** Function that calculates the width of the PeriodBox */
  getPeriodBoxWidth: (positionFrom: number, positionTo: number) => number;
  /** Function that calculates the position of left side of the PeriodBox */
  getPeriodBoxLeft: (positionFrom: number) => string;
  /** The absolute position of top side of the PeriodBox */
  periodBoxTop: number;
  /** The zIndex used to display the PeriodBox */
  zIndex: number;
}

/** Internal Component that displays one Period. */
function PeriodBox({
  period,
  level,
  totalNumberOfLevels,
  getPeriodBoxWidth,
  getPeriodBoxLeft,
  periodBoxTop,
  zIndex,
}: PeriodBoxProps): ReactElement | null {
  const theme = useTheme();
  const commonStyleProps = getCommonStyleProps(theme);

  /* Colors used for the periods.
   * fillColors[i] is used for the Periods on level i.
   * The coefficient can be adjusted to get
   * lighter or darker shades of the main theme color. */
  const fillColors = [
    lighten(period?.fillColor ?? theme.palette.primary.main, 0.8),
    lighten(period?.fillColor ?? theme.palette.primary.main, 0.6),
    lighten(period?.fillColor ?? theme.palette.primary.main, 0.4),
    lighten(period?.fillColor ?? theme.palette.primary.main, 0.2),
  ];

  const periodRelativeHeight = `${calculatePeriodRelativeHeight(
    totalNumberOfLevels,
    level
  )}%`;
  // Default width for the from/to labels is 60px
  const labelFromWidth = `${period?.labelFromWidth ?? 60}px`;
  const labelToWidth = `${period?.labelToWidth ?? 60}px`;
  const labelAlignment = period?.labelAlignment ?? "center";
  const descriptionPlacement = period?.descriptionPlacement ?? "bottom";

  return (
    <>
      {period && period.labelFrom && (
        <Tooltip
          data-cy={`${period.dataCy}PeriodLabelFrom`}
          open={true}
          title={period.labelFrom}
          placement="top"
          arrow
          slotProps={{
            popper: {
              sx: {
                "&[data-popper-reference-hidden]": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
                zIndex,
                maxWidth: labelFromWidth,
              },
            },
            tooltip: {
              style: {
                whiteSpace: "pre-line",
                ...commonStyleProps,
              },
            },
          }}
        >
          <Box
            sx={{
              width: "0px",
              height: periodRelativeHeight,
              position: "absolute",
              left: getPeriodBoxLeft(period.positionFrom),
              top: periodBoxTop,
              zIndex,
            }}
          />
        </Tooltip>
      )}
      {period && !period.isInvisible && (
        <Tooltip
          disableFocusListener={period.description === undefined}
          disableHoverListener={period.description === undefined}
          disableTouchListener={period.description === undefined}
          title={period.description}
          placement={descriptionPlacement}
          arrow
          slotProps={{
            popper: {
              sx: {
                "&[data-popper-reference-hidden]": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
              },
            },
            arrow: {
              style: {
                color: fillColors[level],
              },
            },
            tooltip: {
              style: {
                ...commonStyleProps,
                whiteSpace: "pre-line",
                backgroundColor: fillColors[level],
                color: period.textColor ?? theme.palette.primary.dark,
              },
            },
          }}
        >
          <Box
            display="block"
            data-cy={`${period.dataCy}Period`}
            sx={{
              width: getPeriodBoxWidth(period.positionFrom, period.positionTo),
              height: periodRelativeHeight,
              position: "absolute",
              left: getPeriodBoxLeft(period.positionFrom),
              top: periodBoxTop,
              backgroundColor: fillColors[level],
              color: period.textColor ?? theme.palette.primary.dark,
              textAlign: labelAlignment,
              paddingRight: "10px",
              zIndex,
              alignContent: labelAlignment,
              opacity: period.opacity ?? 1,
              borderRadius: theme.shape.borderRadius,
              overflow: "hidden",
            }}
          >
            <Typography
              variant="h6"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              sx={{ pt: "2px", pl: "4px", pr: "4px" }}
            >
              {period.label}
            </Typography>
            <Typography
              variant="caption"
              component="p"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              sx={{ pb: "2px", pl: "5px", pr: "5px" }}
            >
              {period.subLabel}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {period && period.labelTo && (
        <Tooltip
          data-cy={`${period.dataCy}PeriodLabelTo`}
          open={true}
          title={period.labelTo}
          placement="top"
          arrow
          slotProps={{
            popper: {
              sx: {
                "&[data-popper-reference-hidden]": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
                zIndex,
                maxWidth: labelToWidth,
              },
            },
            tooltip: {
              style: {
                ...commonStyleProps,
                whiteSpace: "pre-line",
              },
            },
          }}
        >
          <Box
            sx={{
              width: "0px",
              height: periodRelativeHeight,
              position: "absolute",
              left: getPeriodBoxLeft(period.positionTo),
              top: periodBoxTop,
              zIndex,
            }}
          />
        </Tooltip>
      )}
    </>
  );
}

interface MarkBoxProps {
  /** The Mark which the MarkBox is rendering */
  mark: Mark | undefined;
  /** Function that calculates the horizontal position of the Mark */
  getMarkBoxLeft: (positionFrom: number) => string;
  /** The zIndex used to display the MarkBox */
  zIndex: number;
  /** Text color for the Mark's label */
  labelTextColor: string;
  /** Fill color for the Mark's label */
  labelFillColor: string;
  /** Fill color for the Mark's label arrow */
  labelArrowColor: string;
}

/** Internal Component that displays one Mark */
function MarkBox({
  mark,
  getMarkBoxLeft,
  zIndex,
  labelTextColor,
  labelFillColor,
  labelArrowColor,
}: MarkBoxProps): ReactElement {
  const theme = useTheme();
  const commonStyleProps = getCommonStyleProps(theme);
  return (
    <>
      {mark && !mark.isInvisible && (
        <Tooltip
          data-cy={`${mark.dataCy}Mark`}
          open={true}
          title={mark.label}
          placement="bottom"
          arrow
          slotProps={{
            popper: {
              sx: {
                "&[data-popper-reference-hidden]": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
                zIndex,
              },
            },
            tooltip: {
              style: {
                ...commonStyleProps,
                backgroundColor: labelFillColor,
                color: labelTextColor,
              },
            },
            arrow: {
              style: {
                color: labelArrowColor,
              },
            },
          }}
        >
          <Box
            sx={{
              width: "0px",
              height: `${mark.relativeVerticalPositionLabel ?? 100}%`,
              position: "absolute",
              left: getMarkBoxLeft(mark.position),
              zIndex,
            }}
          />
        </Tooltip>
      )}
    </>
  );
}
interface CursorBoxProps {
  /** The Cursor which the CursorBox is rendering */
  cursor: Cursor | undefined;
  /** Function that calculates the horizontal position of the Cursor */
  getCursorBoxLeft: (positionFrom: number) => string;
  /** The zIndex used to display the MarkBox */
  zIndex: number;
  /** Height of the vertical hairline that is displayed above the timeline, in pixels */
  heightAbove: number;
  /** Height of the vertical hairline that is displayed under the timeline, in pixels */
  heightBelow: number;
  /** Text color for Label and description. */
  textColor: string;
  /** Background color for vertical hairline, arrows, label and description. */
  fillColor: string;
}

/** Internal Component that displays one Cursor. */
function CursorBox({
  cursor,
  getCursorBoxLeft,
  zIndex,
  heightAbove,
  heightBelow,
  textColor,
  fillColor,
}: CursorBoxProps): ReactElement | null {
  const theme = useTheme();
  const commonStyleProps = getCommonStyleProps(theme);
  const cursorWidth = `${cursor?.cursorWidth ?? 2}px`;
  const tooltipWidth = `${cursor?.tooltipWidth ?? 300}px`;

  const cursorHeightAbove =
    heightAbove * ((cursor?.relativeVerticalPositionDescription ?? 100) / 100);
  const cursorHeightBelow =
    heightBelow * ((cursor?.relativeVerticalPositionLabel ?? 100) / 100);

  return (
    <>
      {cursor && cursor.description && (
        <Tooltip
          data-cy={`${cursor.dataCy}CursorDescription`}
          open={true}
          title={cursor.description}
          placement="top"
          arrow
          slotProps={{
            popper: {
              sx: {
                "&[data-popper-reference-hidden]": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
                zIndex,
                maxWidth: tooltipWidth,
              },
            },
            tooltip: {
              style: {
                ...commonStyleProps,
                whiteSpace: "pre-line",
                color: textColor,
                backgroundColor: fillColor,
              },
            },
            arrow: {
              style: {
                color: fillColor,
              },
            },
          }}
        >
          <Box
            sx={{
              width: cursorWidth,
              height: `${cursorHeightAbove}px`,
              position: "absolute",
              left: getCursorBoxLeft(cursor.position),
              top: `${-cursorHeightAbove}px`,
              zIndex,
              borderLeftWidth: cursorWidth,
              borderLeftStyle: "solid",
              borderLeftColor: fillColor,
            }}
          />
        </Tooltip>
      )}
      {cursor && cursor.label && (
        <Tooltip
          data-cy={`${cursor.dataCy}CursorLabel`}
          open={true}
          title={cursor.label}
          placement="bottom"
          arrow
          slotProps={{
            popper: {
              sx: {
                "&[data-popper-reference-hidden]": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
                zIndex,
              },
            },
            tooltip: {
              style: {
                ...commonStyleProps,
                backgroundColor: fillColor,
                color: textColor,
              },
            },
            arrow: {
              style: {
                color: fillColor,
              },
            },
          }}
        >
          <Box
            sx={{
              width: cursorWidth,
              height: `${cursorHeightBelow}px`,
              position: "absolute",
              left: getCursorBoxLeft(cursor.position),
              zIndex,
              borderLeftWidth: cursorWidth,
              borderLeftStyle: "solid",
              borderLeftColor: fillColor,
            }}
          />
        </Tooltip>
      )}
    </>
  );
}

/**
 * Calculates the relative height of a Period based on it's level.
 *
 * @param totalNumberOfLevels The total number of PeriodLevels. Needed to make
 *   sure that all levels fit. The higher the number of levels, the smaller
 *   the height difference between levels.
 * @param level The level on which the given period is located
 * @returns A number between 100 and 0:
 *   If totalNumberOfLevels is <= 3, the function will return:
 *     * 100 for level 0,
 *     * 75 for level 1,
 *     * 50 for level 2.
 *   If totalNumberOfLevels > 3, then the relative height difference between
 *   the levels will decrease. For example if totalNumberOfLevels = 4,
 *   then the function will return:
 *     * 100 for level 0,
 *     * 80 for level 1,
 *     * 60 for level 2,
 *     * 40 for level 3.
 */
function calculatePeriodRelativeHeight(
  totalNumberOfLevels: number,
  level: number
): number {
  const divider = Math.max(totalNumberOfLevels, 3) + 1;
  return 100 - (100.0 / divider) * level;
}

/** Helper function that creates a set of properties used
 * by several components.
 * @param theme The current application's theme
 * @returns A set of CSS properties
 */
function getCommonStyleProps(theme: Theme) {
  return {
    fontWeight: theme.typography.subtitle2.fontWeight,
    fontSize: theme.typography.subtitle2.fontSize,
    letterSpacing: theme.typography.subtitle2.letterSpacing,
  };
}
